"use client"

import { Field, FieldGroup, FieldLabel } from "@/components/ui/field"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { TabsContent } from "@/components/ui/tabs"

import {
  ANALYSIS_OPTIONS,
  FRAME_LABELS,
  FRAME_RATE_OPTIONS,
  AnalysisType,
  TimeSpan,
} from "./config"
import { SliderWithStops } from "./slider-with-stops"

type TimeSpanRadioGroupProps = {
  value: TimeSpan
  onChange: (value: TimeSpan) => void
}

function TimeSpanRadioGroup({ value, onChange }: TimeSpanRadioGroupProps) {
  return (
    <RadioGroup
      value={value}
      onValueChange={(next) => onChange(next as TimeSpan)}
      className="flex flex-wrap items-center gap-6"
    >
      <div className="flex items-center gap-3">
        <RadioGroupItem value="minute" id="timespan-minute" />
        <Label htmlFor="timespan-minute">Per Minute</Label>
      </div>
      <div className="flex items-center gap-3">
        <RadioGroupItem value="second" id="timespan-second" />
        <Label htmlFor="timespan-second">Per Second</Label>
      </div>
    </RadioGroup>
  )
}

type AnalysisTypeRadioGroupProps = {
  value: AnalysisType
  onChange: (value: AnalysisType) => void
}

function AnalysisTypeRadioGroup({ value, onChange }: AnalysisTypeRadioGroupProps) {
  return (
    <RadioGroup
      value={value}
      onValueChange={(next) => onChange(next as AnalysisType)}
      className="grid gap-3 sm:grid-cols-2"
    >
      {ANALYSIS_OPTIONS.map((option) => {
        const optionId = `analysis-${option.value.replace(/\s+/g, "-")}`
        return (
          <div
            key={option.value}
            className="flex items-center gap-3 rounded-md border p-3"
          >
            <RadioGroupItem value={option.value} id={optionId} />
            <Label htmlFor={optionId}>{option.label}</Label>
          </div>
        )
      })}
    </RadioGroup>
  )
}

type VideoPanelProps = {
  timeSpan: TimeSpan
  onTimeSpanChange: (value: TimeSpan) => void
  frameSelections: Record<TimeSpan, number>
  onFrameSelectionChange: (span: TimeSpan, index: number) => void
  analysisType: AnalysisType
  onAnalysisTypeChange: (value: AnalysisType) => void
}

export function VideoPanel({
  timeSpan,
  onTimeSpanChange,
  frameSelections,
  onFrameSelectionChange,
  analysisType,
  onAnalysisTypeChange,
}: VideoPanelProps) {
  return (
    <TabsContent value="video">
      <Card>
        <CardHeader>
          <CardTitle>Video Processing</CardTitle>
          <CardDescription>
            Moondream processes video by analyzing one frame at a time.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="timespan-minute">Timeframe</FieldLabel>
              <TimeSpanRadioGroup value={timeSpan} onChange={onTimeSpanChange} />
            </Field>
            <SliderWithStops
              id={`frames-per-${timeSpan}`}
              label={FRAME_LABELS[timeSpan]}
              values={FRAME_RATE_OPTIONS[timeSpan]}
              valueIndex={frameSelections[timeSpan] ?? 0}
              onIndexChange={(index) => onFrameSelectionChange(timeSpan, index)}
              fullWidth
            />
            <Field>
              <FieldLabel htmlFor="analysis-query">Type of analysis</FieldLabel>
              <AnalysisTypeRadioGroup
                value={analysisType}
                onChange={onAnalysisTypeChange}
              />
            </Field>
          </FieldGroup>
        </CardContent>
      </Card>
    </TabsContent>
  )
}
